import { memo, useCallback, useContext } from 'react';

import PersonInPlaceEditor from '@/components/PersonInPlaceEditor';
import { FieldTypes } from '@/components/PersonInPlaceEditor/types';
import SSN from '@/components/SSN';
import { memberSex } from '@/constants/memberBasicInfo';
import useConstants from '@/hooks/useConstants';
import { usePronouns } from '@/hooks/useDemographicOptions';
import useTimeZones from '@/hooks/useTimeZones';
import PersonContext from '@/pages/Person/PersonContext';
import SectionHeading from '@/pages/Profile/components/SectionHeading';
import { basicInfoSectionSchema } from '@/pages/Profile/schemas';
import CopyContent from '@/ui/components/CopyContent';
import DataList, { DataRow } from '@/ui/components/DataList';

const BasicInfoSection = () => {
  const { person } = useContext(PersonContext);

  const personFullName = `${person.first_name} ${person.last_name}`;
  const { options: pronouns } = usePronouns();

  const { refetch: getConstants } = useConstants(false);
  const formatJurisdictions = useCallback(async () => {
    const { data } = await getConstants();
    if (data?.data)
      return data.data.jurisdictions.map(({ id, name }) => ({ label: name, value: id }));
    return [];
  }, [getConstants]);

  const { refetch: getTimeZones } = useTimeZones(false);
  const formatTimeZones = useCallback(async () => {
    const { data } = await getTimeZones();
    if (data?.data)
      return data.data.time_zones.map((timezone) => ({ label: timezone, value: timezone }));
    return [];
  }, [getTimeZones]);

  return (
    <div id="basic-info-section" className="relative">
      <SectionHeading text="Basic Info" />
      <PersonInPlaceEditor
        sectionEditor
        schema={basicInfoSectionSchema}
        fields={[
          {
            key: 'first_name',
            label: 'First name',
            type: FieldTypes.TEXT
          },
          {
            key: 'last_name',
            label: 'Last name (required for program enrollment)',
            type: FieldTypes.TEXT
          },
          {
            key: 'jurisdiction_id',
            label: 'Jurisdiction',
            type: FieldTypes.SELECT,
            options: formatJurisdictions
          },
          {
            key: 'birthday',
            label: 'Date of birth',
            type: FieldTypes.DATE
          },
          {
            key: 'deceased_on',
            label: 'Date of death',
            type: FieldTypes.DATE
          },
          {
            key: 'time_zone',
            label: 'Time zone',
            type: FieldTypes.SELECT,
            options: formatTimeZones
          },
          {
            key: 'sex',
            label: 'Sex',
            type: FieldTypes.RADIO,
            options: memberSex.map((sex) => ({ label: sex, value: sex }))
          },
          {
            key: 'pronouns',
            label: 'Pronouns',
            type: FieldTypes.SELECT,
            options: pronouns.map((pronoun) => ({ label: pronoun, value: pronoun }))
          },
          {
            key: 'nickname',
            label: 'Nickname',
            type: FieldTypes.TEXT
          }
        ]}>
        <DataList>
          {!!personFullName.trim() ||
          person.jurisdiction?.name ||
          person.birthday ||
          person.time_zone ||
          person.sex ||
          person.nickname ||
          person.pronouns ? (
            <>
              {!!personFullName.trim() && <DataRow label="Full Name" content={personFullName} />}
              {person.nickname && <DataRow label="Nickname" content={person.nickname} />}
              {person.jurisdiction?.name && (
                <DataRow label="Jurisdiction" content={person.jurisdiction.name} />
              )}
              {person.birthday && <DataRow label="DOB" content={person.birthday} />}
              {person.deceased_on && <DataRow label="Date of Death" content={person.deceased_on} />}
              {person.time_zone && <DataRow label="Time Zone" content={person.time_zone} />}
              {person.sex && <DataRow label="Sex" content={person.sex} />}
              {person.pronouns && <DataRow label="Pronouns" content={person.pronouns} />}
              <DataRow label="Medical Record Number">
                {person.medical_record_number ? (
                  <CopyContent content={person.medical_record_number} />
                ) : (
                  'No Medical Record Number'
                )}
              </DataRow>
            </>
          ) : (
            <DataRow label="None" />
          )}
        </DataList>
      </PersonInPlaceEditor>

      <hr className="mb-4 mt-2" />

      <SSN />
    </div>
  );
};

export default memo(BasicInfoSection);
